import React, { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BackofficeReportsApi from './backofficeReportsApi';
import CompleteWeekPeriodFields, { updateCompleteWeekDate } from './CompleteWeekPeriodFields';

const REQUEST_MS = 30000;
const GENERATION_MS = 300000;
const ALL_CHANNELS_OPTION = '__all_channels__';
const allChannelsSelected = (selected, available) => available.length > 0
  && available.every((channel) => selected.includes(channel.value));
function nextChannels(rawValue, selected, available) {
  const values = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
  if (values.includes(ALL_CHANNELS_OPTION)) {
    return allChannelsSelected(selected, available) ? [] : available.map((channel) => channel.value);
  }
  return values.map(Number);
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const LANGUAGE = { en: 'English', ro: 'Română' };
const STATUS = { ready: 'Ready' };
const EMPTY_FORM = { location: null, analysis_start: '', analysis_end: '', comparison_mode: 'previous_period', channels: [], language: 'en' };
const DATE_LABELS = { analysis_start: 'Analysis start', analysis_end: 'Analysis end', comparison_start: 'Comparison start', comparison_end: 'Comparison end' };
const day = (value) => DATE.test(value) ? Date.parse(`${value}T12:00:00Z`) : NaN;
const validDate = (value) => Number.isFinite(day(value)) && new Date(day(value)).toISOString().slice(0, 10) === value;
const identifier = (value) => typeof value === 'string' ? value.length > 0 : Number.isSafeInteger(value) && value > 0;
const shiftedDate = (value, days) => new Date(day(value) + days * 86400000).toISOString().slice(0, 10);

function initialForm(latestDate) {
  if (!validDate(latestDate)) return EMPTY_FORM;
  const sunday = shiftedDate(latestDate, -new Date(day(latestDate)).getUTCDay());
  return { ...EMPTY_FORM, analysis_start: shiftedDate(sunday, -27), analysis_end: sunday };
}

export function comparisonDates(start, end, mode = 'previous_period') {
  if (!validDate(start) || !validDate(end) || end < start) return {};
  const length = (day(end) - day(start)) / 86400000 + 1;
  const shift = mode === 'previous_period' ? length : mode === 'last_year' ? 364 : NaN;
  if (!Number.isFinite(shift)) return {};
  const earlier = (value) => new Date(day(value) - shift * 86400000).toISOString().slice(0, 10);
  return { comparison_start: earlier(start), comparison_end: earlier(end) };
}

export function validateCommercialAnalysis(form, limits, latestDate) {
  if (!form.location) return 'Select one authorized location.';
  if (!Object.keys(DATE_LABELS).every((key) => validDate(form[key]))) return 'Select valid analysis dates to determine the comparison period.';
  const lengths = ['analysis', 'comparison'].map((period) => (day(form[`${period}_end`]) - day(form[`${period}_start`])) / 86400000 + 1);
  if (lengths.some((length) => length <= 0 || length > limits.max_period_days)) return `Each period must contain 1–${limits.max_period_days} days.`;
  if (lengths[0] !== lengths[1]) return 'The analysis and comparison periods must have equal lengths.';
  if (['analysis', 'comparison'].some((period) => new Date(day(form[`${period}_start`])).getUTCDay() !== 1
      || new Date(day(form[`${period}_end`])).getUTCDay() !== 0)) return 'Use complete weeks: start on Monday and end on Sunday.';
  if (form.analysis_end > latestDate || form.comparison_end > latestDate) return `Use completed weeks through ${latestDate} in Europe/Bucharest.`;
  if (!(form.analysis_end < form.comparison_start || form.comparison_end < form.analysis_start)) return 'The periods must not overlap.';
  if (!form.channels.length || form.channels.some((value) => !Number.isInteger(value) || value < 0 || value > 8)) return 'Select at least one supported channel.';
  if (!LANGUAGE[form.language]) return 'Choose English or Romanian.';
  return '';
}

function validOptions(data) {
  const limits = data?.limits;
  return Array.isArray(data?.locations) && data.locations.every((row) => identifier(row.id) && identifier(row.company_id) && typeof row.name === 'string')
    && Array.isArray(data.channels) && data.channels.length > 0 && data.channels.every((row) => Number.isInteger(row.value) && row.value >= 0 && row.value <= 8 && typeof row.label === 'string')
    && Array.isArray(data.languages) && data.languages.length > 0 && data.languages.every((value) => LANGUAGE[value])
    && limits?.currency === 'RON' && limits.complete_weeks === true
    && Number.isInteger(limits.max_period_days) && limits.max_period_days > 0 && limits.max_period_days <= 84
    && Number.isInteger(limits.max_orders_per_period) && limits.max_orders_per_period > 0 && limits.max_orders_per_period <= 20000
    && Number.isInteger(limits.max_lines) && limits.max_lines > 0 && limits.max_lines <= 100000
    && limits.max_running === 1
    && Number.isFinite(limits.retention_hours) && limits.retention_hours > 0;
}

function validRun(row) {
  return UUID.test(row?.id) && identifier(row.location_id) && typeof row.location_name === 'string'
    && identifier(row.company_id) && LANGUAGE[row.language] && Object.keys(DATE_LABELS).every((key) => validDate(row[key]))
    && Array.isArray(row.channels) && row.channels.every((value) => Number.isInteger(value) && value >= 0 && value <= 8)
    && Object.prototype.hasOwnProperty.call(STATUS, row.status) && typeof row.download_available === 'boolean'
    && Number.isFinite(Date.parse(row.requested_at)) && row.coverage && typeof row.coverage === 'object'
    && Array.isArray(row.coverage.warnings);
}

function sameRequest(row, parameters) {
  return String(row.location_id) === String(parameters.location_id) && String(row.company_id) === String(parameters.company_id)
    && row.language === parameters.language && Object.keys(DATE_LABELS).every((key) => row[key] === parameters[key])
    && [...row.channels].sort().join(',') === [...parameters.channels].sort().join(',');
}

function errorText(error, action = 'load') {
  const status = Number(error?.status);
  const body = error?.responseJSON || {};
  if (status === 401) return 'Your session expired. Sign in again.';
  if (status === 403) return 'You no longer have permission to access these financial reports.';
  if (status === 404) return 'This report or location is no longer available in your permitted scope.';
  if (status === 410) return 'This download has expired or is unavailable. Request a new report if needed.';
  if (status === 409) return action === 'download' ? 'This report is not ready for download. Refresh its status.' : 'Another report is running. Wait for it to finish, then try again.';
  if (status === 400) {
    const fields = { ...DATE_LABELS, location_id: 'Location', company_id: 'Company', channels: 'Channels', language: 'Report language', non_field_errors: 'Selection', detail: 'Selection' };
    const messages = Object.keys(fields).flatMap((key) => {
      const value = body[key];
      return (Array.isArray(value) ? value : typeof value === 'string' ? [value] : [])
        .filter((message) => typeof message === 'string').map((message) => `${fields[key]}: ${message.slice(0, 250)}`);
    });
    return messages.slice(0, 4).join(' ') || 'The server rejected this selection. Check the location, dates, channels and language.';
  }
  if (error?.code === 'invalid_response') return 'The server returned incomplete report information. Refresh to load it again.';
  if (status >= 500) return 'The report service is unavailable. Refresh history before trying again.';
  return action === 'download' ? 'The PDF could not be downloaded. Check your connection and retry.' : 'Report information could not be loaded. Check your connection and retry.';
}

function request(start, onSuccess, onFailure, timeout = REQUEST_MS, deferStart = false) {
  let active = true;
  let pending;
  const failure = (error) => { if (active) { active = false; clearTimeout(timer); onFailure(error); } };
  const timer = setTimeout(() => {
    failure(Object.assign(new Error('Request timed out.'), { code: 'timeout' }));
    pending?.abort?.();
  }, timeout);
  const begin = () => {
    if (!active) return;
    try {
      pending = start();
      Promise.resolve(pending).then((data) => {
        if (!active) return;
        try { onSuccess(data); active = false; clearTimeout(timer); } catch (error) { failure(error); }
      }, failure);
    } catch (error) { failure(error); }
  };
  // Skip the first mount's GET when development Strict Mode replays effects.
  if (deferStart) Promise.resolve().then(begin);
  else begin();
  return () => { active = false; clearTimeout(timer); pending?.abort?.(); };
}

function invalidResponse() { return Object.assign(new Error('Invalid report response.'), { code: 'invalid_response' }); }
function timestamp(value, language = 'en') {
  return value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString(language === 'ro' ? 'ro-RO' : 'en-GB', { timeZone: 'Europe/Bucharest', dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

function reportDate(value, language) {
  return new Date(day(value)).toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-GB', {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function comparisonLabel(run) {
  // Saved exports retain exact dates rather than the original selector value.
  // Name a preset only when both saved boundaries match the existing rule.
  const matches = (mode) => {
    const dates = comparisonDates(run.analysis_start, run.analysis_end, mode);
    return dates.comparison_start === run.comparison_start && dates.comparison_end === run.comparison_end;
  };
  if (matches('previous_period')) return run.language === 'ro' ? 'Perioada precedentă' : 'Previous period';
  if (matches('last_year')) return run.language === 'ro' ? 'Perioada echivalentă din anul trecut' : 'Equivalent period last year';
  return run.language === 'ro' ? 'Perioada de comparație selectată' : 'Selected comparison period';
}

function CoverageNotes({ run }) {
  if (run.status !== 'ready' || run.coverage.warnings.length === 0) return null;
  return <Tooltip title={<Box>{run.coverage.warnings.map((warning, index) =>
    <Typography key={index} variant="caption" display="block">{warning}</Typography>)}</Box>} arrow>
    <IconButton size="small" aria-label={`Coverage notes for ${run.location_name}`} sx={{ p: 0.25, color: '#607a80' }}><InfoOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
  </Tooltip>;
}

function ChannelScope({ channels, supportedChannels = [], language = 'en' }) {
  const romanian = language === 'ro';
  const labels = channels.map((value) => supportedChannels.find((channel) => channel.value === value)?.label || `${romanian ? 'Canal' : 'Channel'} ${value}`);
  const allNine = channels.length === 9 && Array.from({ length: 9 }, (_, value) => value).every((value) => channels.includes(value));
  const summary = !labels.length ? (romanian ? 'Necunoscute' : 'Unknown') : allNine ? (romanian ? 'Toate canalele' : 'All channels') : labels.length <= 2 ? labels.join(', ') : `${labels.length} ${romanian ? 'canale' : 'channels'}`;
  const label = romanian ? 'Canale' : 'Channels';
  return <Tooltip title={labels.length ? `${label}: ${labels.join(', ')}` : (romanian ? 'Canalele nu sunt disponibile' : 'Channel scope unavailable')} arrow>
    <Typography component="span" tabIndex={0} variant="caption" color="text.secondary"
      sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}: {summary}
    </Typography>
  </Tooltip>;
}

function RunPdfAction({ run, downloading, onDownload }) {
  return run.status === 'ready' && run.download_available
    ? <Tooltip title={downloading === run.id ? 'Downloading PDF…' : 'Download PDF'} arrow><span><IconButton size="small"
      onClick={() => onDownload(run)} disabled={Boolean(downloading)} aria-label={`Download PDF for ${run.location_name} (${LANGUAGE[run.language]})`}>
      {downloading === run.id ? <CircularProgress size={18} /> : <DownloadRoundedIcon fontSize="small" />}
    </IconButton></span></Tooltip>
    : <Typography variant="body2" color="text.secondary">{run.status === 'ready' ? run.expires_at && Date.parse(run.expires_at) <= Date.now() ? 'Expired' : 'Unavailable' : '—'}</Typography>;
}

export function initialCommercialAnalysisForm(latestDate) { return initialForm(latestDate); }

export default function CommercialAnalysisDialog({ client, latestDate, onClose, embedded = false }) {
  const compactHistory = useMediaQuery('(max-width:839.95px)');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [form, setForm] = useState(() => initialForm(latestDate));
  const [optionsState, setOptionsState] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [optionsVersion, setOptionsVersion] = useState(0);
  const [historyState, setHistoryState] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(true);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [accessError, setAccessError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unknownRequest, setUnknownRequest] = useState(null);
  const [downloading, setDownloading] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const submitLock = useRef(false);
  const submitCancel = useRef(null);
  const downloadAbort = useRef(null);
  const mounted = useRef(true);
  const clientGeneration = useRef(0);
  const uncertain = useRef(null);
  const options = optionsState?.client === client ? optionsState.data : null;
  const history = historyState?.client === client ? historyState : { results: [], count: 0 };
  const rows = history.results;
  const comparison = comparisonDates(form.analysis_start, form.analysis_end, form.comparison_mode);
  const selection = { ...form, ...comparison };
  const validation = options ? validateCommercialAnalysis(selection, options.limits, latestDate) : '';
  const noLocations = options && options.locations.length === 0;
  const denyAccess = (error) => {
    if ([401, 403].includes(Number(error?.status))) {
      setAccessError(errorText(error)); setOptionsState(null); setHistoryState(null);
    }
  };

  useEffect(() => {
    clientGeneration.current += 1;
    mounted.current = true;
    setForm(initialForm(latestDate)); setHistoryOpen(true); setAccessError(''); setUnknownRequest(null); uncertain.current = null;
    submitLock.current = false; setSubmitting(false); setNotice(''); setSubmitError('');
    setDownloading(''); setDownloadError(''); downloadAbort.current = null;
    return () => { mounted.current = false; submitCancel.current?.(); downloadAbort.current?.abort(); };
  }, [client]);

  useEffect(() => {
    setOptionsLoading(true); setOptionsError(''); setOptionsState(null);
    return request(() => BackofficeReportsApi.getCommercialAnalysisOptionsAsync(client), (data) => {
      if (!validOptions(data)) throw invalidResponse();
      setOptionsState({ client, data }); setOptionsLoading(false);
      setForm((previous) => ({ ...previous, location: null, channels: data.channels.map((channel) => channel.value),
        language: data.languages.includes(previous.language) ? previous.language : data.languages[0] }));
    }, (error) => { setOptionsLoading(false); setOptionsError(errorText(error)); denyAccess(error); }, REQUEST_MS, true);
  }, [client, optionsVersion]);

  useEffect(() => {
    let active = true;
    let cancel;
    const load = () => {
      setHistoryLoading(true);
      cancel = request(() => BackofficeReportsApi.getCommercialAnalysisRunsAsync(client), (data) => {
        if (!Array.isArray(data?.results) || !Number.isInteger(data.count) || data.count < data.results.length || !data.results.every(validRun)) throw invalidResponse();
        if (!active) return;
        setHistoryState({ client, results: data.results, count: data.count }); setHistoryLoading(false); setHistoryError('');
        if (uncertain.current && data.results.some((row) => sameRequest(row, uncertain.current.parameters)
            && !uncertain.current.completedIds.includes(row.id))) {
          uncertain.current = null; setUnknownRequest(null); setSubmitError('');
          setNotice('Your previous request was saved. Download the PDF from history.');
        }
      }, (error) => {
        if (!active) return;
        setHistoryLoading(false); setHistoryError(errorText(error)); denyAccess(error);
      }, REQUEST_MS, true);
    };
    load();
    return () => { active = false; cancel?.(); };
  }, [client, historyVersion]);

  const refreshHistory = () => { setHistoryVersion((value) => value + 1); };
  const change = (key, value) => { setForm((previous) => ({ ...previous, [key]: value })); setSubmitError(''); setNotice(''); };
  const changeDate = (key, value) => {
    const updated = updateCompleteWeekDate(form, key, value, latestDate, options.limits.max_period_days);
    if (!updated) return;
    setForm(updated);
    setSubmitError(''); setNotice('');
  };
  const submit = (event) => {
    event.preventDefault(); setShowValidation(true);
    if (submitLock.current || unknownRequest || !options || optionsLoading
        || historyLoading || historyError || accessError || noLocations || validation) return;
    const parameters = { location_id: form.location.id, company_id: form.location.company_id,
      ...Object.fromEntries(Object.keys(DATE_LABELS).map((key) => [key, selection[key]])), channels: [...form.channels].sort((a, b) => a - b), language: form.language };
    const completedIds = rows.map((row) => row.id);
    submitLock.current = true; setSubmitting(true); setSubmitError(''); setNotice('');
    submitCancel.current = request(() => BackofficeReportsApi.postCommercialAnalysisRunAsync(client, parameters), (run) => {
      if (!validRun(run) || !sameRequest(run, parameters)) throw invalidResponse();
      submitLock.current = false; setSubmitting(false);
      setHistoryState((previous) => ({ client, count: Math.max(previous?.count || 0, 1), results: [run, ...(previous?.client === client ? previous.results.filter((row) => row.id !== run.id) : [])] }));
      setNotice('Report generated. Download the PDF from history.');
      setHistoryOpen(true);
      refreshHistory();
    }, (error) => {
      submitLock.current = false; setSubmitting(false); denyAccess(error);
      const knownFailure = ['execution_failed', 'execution_timeout', 'memory_limit', 'unsupported_data', 'storage_unavailable'].includes(error?.responseJSON?.code);
      if ((!Number(error?.status) || Number(error?.status) >= 500) && !knownFailure) {
        uncertain.current = { parameters, completedIds }; setUnknownRequest(parameters);
        setSubmitError('The request outcome is unknown. Check history before requesting again. If no matching request appears after refreshing, close and reopen this dialog to retry deliberately.');
      } else setSubmitError(knownFailure && typeof error?.responseJSON?.detail === 'string'
        ? error.responseJSON.detail.slice(0, 300) : errorText(error, 'create'));
    }, GENERATION_MS);
  };

  const download = async (run) => {
    if (downloadAbort.current) return;
    const generation = clientGeneration.current;
    const controller = new AbortController(); downloadAbort.current = controller;
    setDownloading(run.id); setDownloadError('');
    const timer = setTimeout(() => controller.abort(), REQUEST_MS);
    try {
      await BackofficeReportsApi.downloadCommercialAnalysisPdf(client, run.id, controller.signal, {
        location_name: run.location_name, analysis_start: run.analysis_start, analysis_end: run.analysis_end,
      });
    } catch (error) {
      if (!mounted.current || generation !== clientGeneration.current) return;
      setHistoryOpen(true);
      setDownloadError(errorText(error, 'download')); denyAccess(error);
      if ([404, 409, 410].includes(Number(error?.status))) refreshHistory();
    } finally {
      clearTimeout(timer);
      if (mounted.current && generation === clientGeneration.current) { setDownloading(''); downloadAbort.current = null; }
    }
  };

  const contents = <>
    {!embedded ? <DialogTitle component="div" sx={{ px: { xs: 2, sm: 3 }, py: 1.75, borderBottom: '1px solid #e0e9e7' }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ width: 37, height: 37, borderRadius: 2, bgcolor: '#e4f2ed', color: '#167769', display: 'grid', placeItems: 'center' }}><AssessmentOutlinedIcon fontSize="small" /></Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id="commercial-analysis-title" component="h2" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#12383d' }}>Commercial Analysis</Typography>
          <Typography variant="caption" color="text.secondary">Analiză comercială · One location · PDF in EN or RO</Typography>
        </Box>
        <Tooltip title="Generate a PDF for one location. Keep this window open during generation; finished files remain in history for 24 hours." arrow>
          <IconButton size="small" aria-label="About Commercial Analysis" sx={{ color: '#607a80' }}><InfoOutlinedIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
    </DialogTitle> : null}
    <DialogContent sx={{ bgcolor: '#f6f9f8', px: { xs: 2, sm: 3 }, pt: '16px !important', pb: 2,
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        {accessError ? <Alert severity="error">{accessError}</Alert> : null}
        {optionsLoading ? <Box role="status" aria-label="Loading permitted locations and limits" sx={{ bgcolor: '#fff', border: '1px solid #dce7e5', borderRadius: 2.5, p: 2 }}>
          <Skeleton width={110} height={24} /><Stack direction="row" spacing={1.5}><Skeleton variant="rounded" width={300} height={40} /><Skeleton variant="rounded" width={140} height={40} /></Stack>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}><Skeleton variant="rounded" width={155} height={40} /><Skeleton variant="rounded" width={155} height={40} /><Skeleton variant="rounded" width={220} height={40} /></Stack>
        </Box> : null}
        {optionsError && !accessError ? <Alert severity="error" action={<Button color="inherit" onClick={() => setOptionsVersion((value) => value + 1)}>Retry</Button>}>{optionsError}</Alert> : null}
        {noLocations ? <Alert severity="info">No locations are available in your authorized financial-report scope.</Alert> : null}
        {options && !accessError ? <Box component="form" onSubmit={submit} sx={{ bgcolor: '#fff', border: '1px solid #dce7e5', borderRadius: 2.5, px: 2, py: 1.5, boxShadow: '0 2px 8px rgba(18, 56, 61, 0.04)' }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700, color: '#12383d' }}>New report</Typography>
              <Tooltip title={`Complete Monday–Sunday weeks from the last three years, up to ${options.limits.max_period_days} days. Comparisons use equal-length periods; last-year aligns weekdays. RON · Europe/Bucharest. Up to ${options.limits.max_orders_per_period.toLocaleString('en-GB')} recorded orders and ${options.limits.max_lines.toLocaleString('en-GB')} commercial lines per period. Files expire after ${options.limits.retention_hours} hours. One report can run at a time. Existing PDFs keep their original language.`} arrow>
                <IconButton size="small" aria-label="Report limits and date guidance" sx={{ color: '#607a80', p: 0.5 }}><InfoOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
              </Tooltip>
              {compactHistory ? <Button type="button" size="small" aria-controls="commercial-overview-filters" aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((open) => !open)} sx={{ ml: 'auto !important', textTransform: 'none', whiteSpace: 'nowrap' }}>
                {filtersOpen ? 'Hide filters' : 'Show filters'}
              </Button> : null}
            </Stack>
            {compactHistory && !filtersOpen ? <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {form.location?.name || 'No location selected'} · {form.analysis_start} – {form.analysis_end}
            </Typography> : null}
            <Stack id="commercial-overview-filters" spacing={1.25} sx={{ display: !compactHistory || filtersOpen ? 'flex' : 'none' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Autocomplete options={options.locations} value={form.location} onChange={(_, value) => change('location', value)} disabled={submitting || Boolean(unknownRequest) || noLocations}
                sx={{ width: { xs: '100%', sm: 300 }, maxWidth: '100%' }}
                getOptionLabel={(option) => option.name} isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                renderInput={(params) => <TextField {...params} label="Authorized location" size="small" required
                  InputLabelProps={{ ...params.InputLabelProps, ...(form.location ? { shrink: true } : {}) }} />} />
              <TextField select label="Report language" value={form.language} onChange={(event) => change('language', event.target.value)} size="small" disabled={submitting || Boolean(unknownRequest)} sx={{ width: { xs: '100%', sm: 145 } }}>
                {options.languages.map((value) => <MenuItem key={value} value={value}>{LANGUAGE[value]}</MenuItem>)}
              </TextField>
              <Box sx={{ width: { xs: '100%', sm: 220 }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormControl size="small" disabled={submitting || Boolean(unknownRequest)} sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="commercial-channels-label" shrink>Channels</InputLabel>
                  <Select multiple displayEmpty labelId="commercial-channels-label" label="Channels" value={form.channels}
                    onChange={(event) => change('channels', nextChannels(event.target.value, form.channels, options.channels))}
                    renderValue={(values) => !values.length ? 'No channels' : allChannelsSelected(values, options.channels) ? 'All channels' : values.length <= 2
                      ? options.channels.filter((channel) => values.includes(channel.value)).map((channel) => channel.label).join(', ') : `${values.length} channels`}>
                    <MenuItem value={ALL_CHANNELS_OPTION} disabled={!options.channels.length} sx={{ fontWeight: 700, borderBottom: '1px solid #edf2f1' }}>
                      {allChannelsSelected(form.channels, options.channels) ? 'Clear all channels' : 'Select all channels'}
                    </MenuItem>
                    {options.channels.map((channel) => <MenuItem key={channel.value} value={channel.value}><Checkbox checked={form.channels.includes(channel.value)} size="small" />{channel.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <CompleteWeekPeriodFields form={form} latestDate={latestDate} maxPeriodDays={options.limits.max_period_days}
              comparison={comparison} onDateChange={changeDate} onModeChange={(value) => change('comparison_mode', value)} disabled={submitting || Boolean(unknownRequest)}
              action={<Button type="submit" variant="contained" disableElevation disabled={submitting || Boolean(unknownRequest) || historyLoading || Boolean(historyError) || noLocations} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ ml: { sm: 'auto' }, bgcolor: '#167769', borderRadius: 1.5, px: 2, minHeight: 40, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#105f54' } }}>{submitting ? 'Generating report…' : 'Generate PDF'}</Button>} />
            </Stack>
            {showValidation && validation ? <Alert severity="warning">{validation}</Alert> : null}
            {submitError ? <Alert severity={unknownRequest ? 'warning' : 'error'} action={unknownRequest ? <Button color="inherit" onClick={refreshHistory} disabled={historyLoading}>Check history</Button> : undefined}>{submitError}</Alert> : null}
            {notice ? <Alert severity="success">{notice}</Alert> : null}
          </Stack>
        </Box> : null}
        <Box sx={{ bgcolor: '#fff', border: '1px solid #dce7e5', borderRadius: 2.5, overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(18, 56, 61, 0.04)', display: 'flex', flexDirection: 'column',
          flex: historyOpen ? 1 : 'none', minHeight: historyOpen ? 160 : undefined }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
            <Button aria-expanded={historyOpen} aria-controls="commercial-history-content" onClick={() => setHistoryOpen((open) => !open)}
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: historyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }} />}
              sx={{ color: '#12383d', textTransform: 'none', fontWeight: 700, justifyContent: 'space-between', px: 0.5, minWidth: 0 }}>
              {historyOpen ? 'Hide report history' : 'Show report history'}
            </Button>
            {!historyLoading && !historyError ? <Chip size="small" variant="outlined" label={`${history.count} ${history.count === 1 ? 'report' : 'reports'}`} sx={{ height: 23, color: '#536a75', borderColor: '#dce7e5' }} /> : null}
            {!historyLoading && !historyError && !accessError && !rows.length ? <Typography variant="caption" color="text.secondary">No completed exports available.</Typography> : null}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh completed exports" arrow><span><IconButton size="small" aria-label="Refresh history" onClick={refreshHistory} disabled={historyLoading || Boolean(accessError)} sx={{ color: '#607a80' }}><RefreshRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          </Stack>
          {historyError && !accessError ? <Alert severity="error" sx={{ mx: 1.5, mb: 1 }}>{historyError}</Alert> : null}
          {downloadError && !accessError ? <Alert severity="error" sx={{ mx: 1.5, mb: 1 }}>{downloadError}</Alert> : null}
          {historyOpen ? <Box id="commercial-history-content" sx={{ px: 1.5, pb: 1.5, borderTop: '1px solid #edf2f1',
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Tooltip title="Created times use Europe/Bucharest. Completed files are available for 24 hours." arrow>
                <IconButton size="small" aria-label="About report history dates and retention" sx={{ alignSelf: 'flex-end', p: 0.25, color: '#607a80' }}><InfoOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
              </Tooltip>
              {historyLoading && !rows.length ? <Typography role="status" variant="body2">Loading report history…</Typography> : null}
              {rows.length > 0 && !accessError && compactHistory ? <Box role="region" aria-label="Commercial analysis saved reports" tabIndex={0}
                sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}>
                <Stack role="list" aria-label="Commercial analysis report history" spacing={1} sx={{ py: 0.5 }}>
                  {rows.map((run) => <Box role="listitem" key={run.id} lang={run.language} sx={{ p: 1, border: '1px solid #dce7e5', borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, fontWeight: 700, overflowWrap: 'anywhere' }}>{run.location_name}</Typography>
                      <RunPdfAction run={run} downloading={downloading} onDownload={download} />
                    </Stack>
                    <Typography variant="caption" display="block">{run.language === 'ro' ? 'Analizată' : 'Analysed'}: {reportDate(run.analysis_start, run.language)} – {reportDate(run.analysis_end, run.language)}</Typography>
                    <Typography variant="caption" display="block">{run.language === 'ro' ? 'Comparație' : 'Comparison'}: {comparisonLabel(run)} · {reportDate(run.comparison_start, run.language)} – {reportDate(run.comparison_end, run.language)}</Typography>
                    <ChannelScope channels={run.channels} supportedChannels={options?.channels} language={run.language} />
                    <Typography variant="caption" color="text.secondary" display="block">{run.language === 'ro' ? 'Monedă' : 'Currency'}: RON</Typography>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.25 }}>
                      <Chip size="small" label={STATUS[run.status]} color="success" variant="outlined" />
                      <CoverageNotes run={run} />
                      <Typography variant="caption" color="text.secondary">{LANGUAGE[run.language]} · {timestamp(run.requested_at, run.language)}</Typography>
                    </Stack>
                  </Box>)}
                </Stack>
              </Box> : null}
              {rows.length > 0 && !accessError && !compactHistory ? <TableContainer role="region" aria-label="Commercial analysis saved reports" tabIndex={0}
                sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', scrollbarGutter: 'stable' }}>
                <Table stickyHeader size="small" aria-label="Commercial analysis report history" sx={{ tableLayout: 'fixed' }}>
            <TableHead><TableRow>{[['Location', '17%'], ['Analysed', '18%'], ['Comparison', '18%'], ['Lang.', '7%'], ['Created', '17%'], ['Status', '16%'], ['PDF', '7%']].map(([heading, width]) => <TableCell key={heading} align={heading === 'PDF' ? 'center' : 'left'} sx={{ width, px: 1, fontWeight: 700, bgcolor: '#f8fafc' }}>{heading}</TableCell>)}</TableRow></TableHead>
            <TableBody>{rows.map((run) => <TableRow key={run.id} lang={run.language}>
              <TableCell sx={{ px: 1, overflowWrap: 'anywhere' }}>{run.location_name}
                <ChannelScope channels={run.channels} supportedChannels={options?.channels} language={run.language} />
                <Typography variant="caption" color="text.secondary" display="block">{run.language === 'ro' ? 'Monedă' : 'Currency'}: RON</Typography>
              </TableCell>
              <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>{reportDate(run.analysis_start, run.language)}<br />{reportDate(run.analysis_end, run.language)}</TableCell>
              <TableCell sx={{ px: 1 }}><Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.25 }}>{comparisonLabel(run)}</Typography>
                <Box sx={{ whiteSpace: 'nowrap' }}>{reportDate(run.comparison_start, run.language)}<br />{reportDate(run.comparison_end, run.language)}</Box>
              </TableCell>
              <TableCell sx={{ px: 1 }}><Tooltip title={LANGUAGE[run.language]} arrow><Typography component="span" tabIndex={0} variant="body2">{run.language.toUpperCase()}</Typography></Tooltip></TableCell><TableCell sx={{ px: 1, overflowWrap: 'anywhere' }}>{timestamp(run.requested_at, run.language)}</TableCell>
              <TableCell sx={{ px: 1 }}><Stack direction="row" spacing={0.25} alignItems="center">
                <Chip size="small" label={STATUS[run.status]} color="success" variant="outlined" />
                <CoverageNotes run={run} /></Stack>
              </TableCell>
              <TableCell align="center" sx={{ px: 0.25, whiteSpace: 'nowrap' }}><RunPdfAction run={run} downloading={downloading} onDownload={download} /></TableCell>
            </TableRow>)}</TableBody>
              </Table></TableContainer> : null}
              {history.count > rows.length ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Showing the latest {rows.length} of {history.count} completed exports.</Typography> : null}
            </Box> : null}
        </Box>
      </Stack>
    </DialogContent>
    {!embedded ? <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1, borderTop: '1px solid #e0e9e7' }}>
      <Tooltip title="If the connection is interrupted, check history before generating again." arrow>
        <IconButton size="small" aria-label="About interrupted requests" sx={{ mr: 'auto', color: '#607a80' }}><InfoOutlinedIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Button onClick={onClose} sx={{ color: '#12383d' }}>Close</Button>
    </DialogActions> : null}
  </>;
  return embedded ? <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{contents}</Box>
    : <Dialog open onClose={onClose} maxWidth={false} aria-labelledby="commercial-analysis-title"
      PaperProps={{ sx: { width: 'min(920px, calc(100vw - 32px))', height: 'min(820px, calc(100vh - 32px))',
        maxHeight: 'calc(100vh - 32px)', m: 2, borderRadius: 3, border: '1px solid #dce7e5',
        boxShadow: '0 24px 64px rgba(18, 56, 61, 0.22)', overflow: 'hidden' } }}>{contents}</Dialog>;
}
